import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// ============================================
// ThemeContext – Dark-/Light-Mode für die gesamte App.
//
// Mechanik: Auf <html> wird die Klasse 'dark' bzw. 'light' gesetzt.
// - Tailwind (darkMode: 'class') und NextUI (Themes via .dark/.light,
//   inkl. Portals auf document.body) reagieren auf die Klasse am <html>.
// - Der Light-Mode wird über globale CSS-Overrides in src/index.css
//   (Selektoren unter `html.light`) realisiert, da die App-Utilities
//   hartkodiert dunkel sind.
// - Persistenz in localStorage ('dc-theme'); ein Inline-Script in
//   public/index.html setzt die Klasse bereits VOR dem ersten Paint,
//   um ein Aufblitzen des falschen Themes (FOUC) zu vermeiden.
// ============================================

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'dc-theme';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const readStoredTheme = (): Theme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage nicht verfügbar -> Default
  }
  return 'dark';
};

const applyThemeToDocument = (theme: Theme) => {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
  root.style.colorScheme = theme;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  // Klasse am <html> synchron halten + persistieren
  useEffect(() => {
    applyThemeToDocument(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Speichern fehlgeschlagen -> ignorieren
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next === 'light' ? 'light' : 'dark');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(
    () => ({ theme, toggleTheme, setTheme }),
    [theme, toggleTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme muss innerhalb eines ThemeProvider verwendet werden');
  }
  return ctx;
};

export default ThemeContext;
